// Pure desktop-grid geometry. No DOM, no imports — unit-tested via node --test
// and consumed by DesktopGrid.tsx under Vite. JSDoc types keep TS callers happy.
// KEEP IN SYNC with ./desktop-grid-geometry.d.mts, which hand-mirrors these
// signatures for the strict shell tsconfig (no allowJs).

/**
 * @typedef {{ id:string, col:number, row:number, cols:number, rows:number }} Cell
 */

/** True if a cols×rows rect at (col,row) is in-bounds and unoccupied. `movingId` is excluded. */
export function fits(items, col, row, cols, rows, gridCols, movingId) {
  if (col < 0 || row < 0) return false;
  if (col + cols > gridCols) return false;
  for (const it of items) {
    if (movingId && it.id === movingId) continue;
    const overlapX = col < it.col + it.cols && it.col < col + cols;
    const overlapY = row < it.row + it.rows && it.row < row + rows;
    if (overlapX && overlapY) return false;
  }
  return true;
}

/** First free origin in reading order (row-major). Grid grows downward (unbounded rows). */
export function firstFree(items, cols, rows, gridCols) {
  const maxRow = items.reduce((m, it) => Math.max(m, it.row + it.rows), 0) + rows + 1;
  for (let row = 0; row <= maxRow; row++) {
    for (let col = 0; col + cols <= gridCols; col++) {
      if (fits(items, col, row, cols, rows, gridCols)) return { col, row };
    }
  }
  return { col: 0, row: 0 };
}

/**
 * Display layout for a given grid width — PURE, and deliberately non-mutating.
 *
 * Stored col/row is the user's *intent*, written only when they drag a tile.
 * Clamping that intent into a narrow viewport and persisting it is lossy: the
 * original arrangement is gone once the window widens again. So the clamp lives
 * here, at render time, and the caller keeps the untouched intent. Same items +
 * same gridCols always yield the same layout, which is what makes
 * narrow → wide round-trip cleanly.
 *
 * @param {Cell[]} items stored items (never modified)
 * @param {number} gridCols current width in cells
 * @returns {Cell[]} items with display col/row
 */
export function layoutForWidth(items, gridCols) {
  const placed = [];
  for (const it of items) {
    const maxCol = Math.max(0, gridCols - it.cols);
    const wantCol = Math.min(Math.max(0, it.col), maxCol);
    const wantRow = Math.max(0, it.row);
    const free = fits(placed, wantCol, wantRow, it.cols, it.rows, gridCols);
    const dest = free
      ? { col: wantCol, row: wantRow }
      : nearestFit(placed, it.id, wantCol, wantRow, it.cols, it.rows, gridCols);
    placed.push({ ...it, col: dest.col, row: dest.row });
  }
  return placed;
}

/** Nearest free origin to a (possibly out-of-range) target, spiraling by Chebyshev rings. */
export function nearestFit(items, movingId, col, row, cols, rows, gridCols) {
  const maxCol = Math.max(0, gridCols - cols);
  let tc = Math.min(Math.max(0, col), maxCol);
  let tr = Math.max(0, row);
  if (fits(items, tc, tr, cols, rows, gridCols, movingId)) return { col: tc, row: tr };
  for (let radius = 1; radius <= gridCols + 32; radius++) {
    for (let r = tr - radius; r <= tr + radius; r++) {
      for (let c = tc - radius; c <= tc + radius; c++) {
        const onRing = Math.max(Math.abs(c - tc), Math.abs(r - tr)) === radius;
        if (!onRing) continue;
        if (fits(items, c, r, cols, rows, gridCols, movingId)) return { col: c, row: r };
      }
    }
  }
  return { col: tc, row: tr };
}
